import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { format, subDays, startOfQuarter, startOfYear } from 'date-fns';
import { cn } from '@/lib/utils';
import { DateRange } from 'react-day-picker';

export type DatePreset = 'last7' | 'last30' | 'last60' | 'last90' | 'qtd' | 'ytd' | 'custom';

interface DateRangeSelectorProps {
  startDate: Date;
  endDate: Date;
  onDateChange: (start: Date, end: Date) => void;
}

const presets: { value: DatePreset; label: string }[] = [
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'last60', label: 'Last 60 Days' },
  { value: 'last90', label: 'Last 90 Days' },
  { value: 'qtd', label: 'Quarter to Date' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'custom', label: 'Custom' },
];

function getPresetDates(preset: DatePreset): { start: Date; end: Date } {
  const today = new Date();
  switch (preset) {
    case 'last7':
      return { start: subDays(today, 7), end: today };
    case 'last30':
      return { start: subDays(today, 30), end: today };
    case 'last60':
      return { start: subDays(today, 60), end: today };
    case 'last90':
      return { start: subDays(today, 90), end: today };
    case 'qtd':
      return { start: startOfQuarter(today), end: today };
    case 'ytd':
      return { start: startOfYear(today), end: today };
    default:
      return { start: subDays(today, 90), end: today };
  }
}

export function DateRangeSelector({ startDate, endDate, onDateChange }: DateRangeSelectorProps) {
  const [activePreset, setActivePreset] = useState<DatePreset>('last90');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startDate,
    to: endDate,
  });

  const handlePresetClick = (preset: DatePreset) => {
    setActivePreset(preset);
    if (preset === 'custom') {
      setIsCalendarOpen(true);
    } else {
      const { start, end } = getPresetDates(preset);
      onDateChange(start, end);
      setDateRange({ from: start, to: end });
    }
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      onDateChange(range.from, range.to);
      setIsCalendarOpen(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {presets.map((preset) => (
          preset.value === 'custom' ? (
            <Popover key={preset.value} open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={activePreset === 'custom' ? 'default' : 'outline'}
                  size="sm"
                  className="gap-2"
                >
                  <CalendarIcon className="h-4 w-4" />
                  {preset.label}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  defaultMonth={startDate}
                  selected={dateRange}
                  onSelect={handleDateRangeChange}
                  numberOfMonths={2}
                  disabled={(date) => date > new Date()}
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Button
              key={preset.value}
              variant={activePreset === preset.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePresetClick(preset.value)}
            >
              {preset.label}
            </Button>
          )
        ))}
      </div>
      
      <p className="text-sm text-muted-foreground">
        Showing data for:{' '}
        <span className="font-medium text-foreground">
          {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
        </span>
      </p>
    </div>
  );
}
